import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Botao } from '../../componentes/base';
import { Vazio } from '../../componentes/estados';
import { Formulario, Selecao, type DefCampo } from '../../componentes/formulario';
import { Drawer } from '../../componentes/sobreposicao';
import { Tabela, type Coluna } from '../../componentes/tabela';
import { useToast } from '../../componentes/toast';
import { api, getTodos } from '../../nucleo/api';
import { hojeIso, nomePessoa } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import { invalidarAusencias, type SaldoFuncionario, type Tipo } from './tipos';

/** Saldos do ano por funcionário e tipo com regra de saldo: direito, usado, em aberto e disponível; ajuste do direito. */
export function Saldos() {
  const { t } = useTranslation('ausencias');
  const s = useSessao();
  const atual = Number(hojeIso().slice(0, 4));
  const [ano, setAno] = useState(atual);
  const [aberto, setAberto] = useState<SaldoFuncionario | null>(null);
  const colunas: Coluna<SaldoFuncionario>[] = [
    { chave: 'nome', rotulo: t('campos.funcionario'), principal: true, render: (f) => <span className="font-medium">{nomePessoa(f.nome)}</span> },
    {
      chave: 'saldos',
      rotulo: t('campos.saldos'),
      render: (f) =>
        f.saldos.length ? (
          <ul className="flex flex-col gap-0.5">
            {f.saldos.map((x) => (
              <li key={x.tipo_id} className="text-sm">
                <span className="text-tinta-2">{x.tipo_nome}: </span>
                <span className="font-mono text-tinta-1">{x.disponivel}</span>
                <span className="text-tinta-3">
                  {' '}
                  / {x.direito}
                  {x.ajustado ? ` (${t('ajustado')})` : ''} · {t('campos.usado')} {x.usado} · {t('campos.pendente')} {x.pendente}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          '-'
        ),
    },
  ];
  const anos = [atual - 1, atual, atual + 1];
  return (
    <>
      <Tabela<SaldoFuncionario>
        tela="ausencias.saldos"
        caminho="/ausencias/saldos"
        params={{ ano }}
        chaveConsulta={['ausencias', 'saldos', ano]}
        colunas={colunas}
        aoClicar={s.pode('ausencias.editar') ? (f) => setAberto(f) : undefined}
        filtros={{ ano: String(ano) }}
        aoAplicarFiltros={(f) => setAno(anos.includes(Number(f.ano)) ? Number(f.ano) : atual)}
        filtrosExtra={<Selecao aria-label={t('campos.ano')} value={String(ano)} onChange={(e) => setAno(Number(e.target.value))} opcoes={anos.map((a) => ({ valor: String(a), rotulo: String(a) }))} />}
        vazio={<Vazio texto={t('vazio.saldos')} />}
      />
      {aberto && <AjustarSaldo funcionario={aberto} ano={ano} aoFechar={() => setAberto(null)} />}
    </>
  );
}

export function AjustarSaldo({ funcionario, ano, aoFechar }: { funcionario: SaldoFuncionario; ano: number; aoFechar: () => void }) {
  const { t } = useTranslation('ausencias');
  const { t: tn } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const tipos = useQuery({ queryKey: ['ausencias', 'tipos', 'ativos'], queryFn: () => getTodos<Tipo>('/ausencias/tipos', { ativo: 'true' }) });
  const campos: DefCampo[] = [
    { nome: 'tipo_id', rotulo: t('campos.tipo'), tipo: 'selecao', obrigatorio: true, opcoes: (tipos.data?.itens ?? []).map((x) => ({ valor: x.id, rotulo: x.nome })) },
    { nome: 'direito_dias', rotulo: t('campos.direito'), tipo: 'numero', min: 0, max: 366, passo: 1, ajuda: t('campos.direitoAjuda') },
  ];
  return (
    <Drawer
      aberto
      aoFechar={aoFechar}
      titulo={`${t('acoes.ajustar')} · ${nomePessoa(funcionario.nome)} · ${ano}`}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.cancelar')}
          </Botao>
          <Botao variante="primario" type="submit" form="form-saldo-ausencia">
            {tn('comum.salvar')}
          </Botao>
        </>
      }
    >
      <Formulario
        key={tipos.data ? 'pronto' : 'carregando'}
        id="form-saldo-ausencia"
        campos={campos}
        inicial={{ tipo_id: funcionario.saldos[0]?.tipo_id }}
        aoEnviar={async (v) => {
          const direito = v.direito_dias === '' || v.direito_dias === null || v.direito_dias === undefined ? null : Number(v.direito_dias);
          await api('PUT', '/ausencias/saldos', { funcionario_id: funcionario.id, tipo_id: v.tipo_id, ano, direito_dias: direito });
          await invalidarAusencias(qc);
          toast.sucesso(tn('comum.salvo'));
          aoFechar();
        }}
      />
    </Drawer>
  );
}
